// EvePresenceDrawer — der globale EVE-Drawer der auf JEDER Page (außer Markt)
// am unteren Rand sitzt. Zwei States:
//
//   - collapsed: schmaler Composer-Streifen mit Avatar + Input + Toggle.
//                Doc kann sofort losschreiben.
//   - expanded:  Chat-Panel über dem Page-Content (~60vh max), mit Verlauf
//                + Composer + Quick-Actions + clear-button.
//
// Per-Page-Chat: nutzt useLocation.pathname als pageKey → jede Page hat
// eigenen Verlauf. Pre-Filled-Frage vom „Frag EVE"-Button wird beim Mount
// gelesen (consumePending) und automatisch gesendet — inkl. evtl. Options
// wie displayText / noLiveData.
//
// Doc-Anweisung 2026-05-15: „SIE IST ALLGEGENWÄRTIG"

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, Link2, Sparkles, Trash2, X } from 'lucide-react';
import { useRunEve } from '@/hooks/use-run-eve';
import {
  selectMessagesFor,
  useEveChatStore,
  type ChatMessage,
} from '@/stores/eve-chat-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import { useEvePageContextStore, selectCurrentPageContext } from '@/stores/eve-page-context-store';
import { MarkdownText } from '@/lib/markdown';
import { useAuth } from '@/hooks/use-auth';

export function EvePresenceDrawer(): ReactElement | null {
  const location = useLocation();
  const pageKey = location.pathname;

  // ALLE Hooks MÜSSEN vor jedem early-return aufgerufen werden (Rules of Hooks).
  const isOpen = useEveDrawerStore((s) => s.isOpen);
  const openDrawer = useEveDrawerStore((s) => s.open);
  const closeDrawer = useEveDrawerStore((s) => s.close);
  const toggle = useEveDrawerStore((s) => s.toggle);
  const pendingPrompt = useEveDrawerStore((s) => s.pendingPrompt);
  const consumePending = useEveDrawerStore((s) => s.consumePending);

  const { send } = useRunEve();
  const messages = useEveChatStore(selectMessagesFor(pageKey));
  const clearChat = useEveChatStore((s) => s.clear);
  const pageContext = useEvePageContextStore(selectCurrentPageContext);
  const { profile } = useAuth();
  const reduceMotion = useReducedMotion();

  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  // Nur automatisch nachscrollen, wenn der User ohnehin unten steht — sonst
  // reisst es ihn beim Lesen nach unten, waehrend EVE noch schreibt.
  const isAtBottomRef = useRef(true);
  const handleChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // Pre-filled Prompt (vom Frag-EVE-Button) abarbeiten. Reagiert auf
  // pendingPrompt-Änderungen — also auch wenn Doc auf der gleichen Page
  // mehrere Frag-EVE-Buttons hintereinander klickt.
  useEffect(() => {
    if (!pendingPrompt) return;
    const consumed = consumePending();
    if (!consumed) return;
    void send({
      agentKey: 'direct',
      userMsg: consumed.prompt,
      pageKey,
      displayText: consumed.options.displayText,
      noLiveData: consumed.options.noLiveData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt, pageKey]);

  // Auto-scroll Chat bei neuen Messages (claude.ai-Style) — aber nur, wenn der
  // User unten steht. Hat er hochgescrollt um zu lesen, bleibt die Position.
  useEffect(() => {
    if (!isOpen) return;
    if (!isAtBottomRef.current) return;
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, isOpen]);

  const pageTitle = pageContext?.title ?? formatPageKey(pageKey);

  const handleSubmit = async (text: string) => {
    const t = text.trim();
    if (!t || submitting) return;
    setSubmitting(true);
    setDraft('');
    try {
      // Wenn Drawer collapsed war und Doc hier einfach in den Input getippt
      // hat — automatisch expanded öffnen damit er die Antwort sieht.
      if (!isOpen) openDrawer();
      await send({ agentKey: 'direct', userMsg: t, pageKey });
    } finally {
      setSubmitting(false);
    }
  };

  const slideVariants = useMemo(
    () =>
      ({
        hidden: { y: '100%', opacity: reduceMotion ? 1 : 0 },
        visible: {
          y: 0,
          opacity: 1,
          transition: { type: 'spring' as const, stiffness: 320, damping: 32, mass: 0.7 },
        },
        exit: {
          y: '100%',
          opacity: reduceMotion ? 1 : 0,
          transition: { duration: 0.2 },
        },
      }) as const,
    [reduceMotion],
  );

  // EVE selbst zeigt sich nicht auf /markt — dort ist sie schon Vollbild.
  // /community hat eigenen EVE-Composer im Chat-Stream (Slash-Command + @eve).
  // /academy hat einen eigenen EVE-Tutor-Chat, /coaching braucht keinen EVE-Chat
  // — der globale Drawer waere dort redundant.
  // Early-return NACH allen Hooks (Rules of Hooks).
  if (
    pageKey === '/markt' ||
    pageKey === '/community' ||
    pageKey === '/academy' ||
    pageKey === '/coaching'
  )
    return null;

  return (
    <>
      {/* Expanded Chat-Panel */}
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            key="eve-panel"
            variants={slideVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="pointer-events-none fixed inset-x-0 bottom-14 z-40 flex justify-center"
          >
            <div className="pointer-events-auto mx-3 flex h-[78vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-neutral-950/95 via-neutral-900/95 to-neutral-950/95 shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.8)] backdrop-blur-xl">
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between border-b border-white/5 px-4 py-3">
                <div className="flex items-center gap-2">
                  <img
                    src="/eve-satoshi.png"
                    alt="EVE"
                    className="size-7 shrink-0 rounded-full object-cover ring-1 ring-amber-500/40"
                  />
                  <div>
                    <div className="text-sm font-medium text-neutral-100">EVE · {pageTitle}</div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {messages.length} Nachricht{messages.length === 1 ? '' : 'en'} · page-spezifisch
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => clearChat(pageKey)}
                      className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-rose-500/10 hover:text-rose-300"
                      title="Verlauf für diese Page leeren"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-white/5 hover:text-neutral-200"
                    title="Drawer schließen"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </div>

              {/* Chat-History */}
              <div
                ref={chatScrollRef}
                onScroll={handleChatScroll}
                className="scroll-hidden flex-1 overflow-y-auto px-4 py-3"
              >
                {messages.length === 0 ? (
                  <EmptyHint pageTitle={pageTitle} pageContext={pageContext?.summary ?? null} />
                ) : (
                  <div className="space-y-3">
                    {messages.map((m) => (
                      <DrawerChatBubble
                        key={m.id}
                        message={m}
                        userInitial={profile?.nickname?.[0] ?? 'D'}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Composer im expanded-Panel */}
              <div className="shrink-0 border-t border-white/5 bg-neutral-950/60 p-3">
                <DrawerComposer
                  value={draft}
                  onChange={setDraft}
                  onSubmit={handleSubmit}
                  disabled={submitting}
                  placeholder={`Frag EVE zu ${pageTitle}…`}
                />
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Collapsed Bar — IMMER sichtbar am unteren Rand */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center">
        <div className="pointer-events-auto mx-3 mb-3 flex w-full max-w-5xl items-center gap-2 rounded-2xl border border-amber-500/20 bg-neutral-950/85 px-3 py-2 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] backdrop-blur-xl">
          <button
            type="button"
            onClick={toggle}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-neutral-900/80 px-2 py-1.5 text-[11px] text-neutral-300 transition-colors hover:border-amber-500/30 hover:bg-amber-500/10 hover:text-amber-200"
            title={isOpen ? 'Chat-Panel einklappen' : 'Chat-Panel ausklappen'}
          >
            <img
              src="/eve-satoshi.png"
              alt="EVE"
              className="size-4 rounded-full object-cover"
            />
            EVE
            {isOpen ? <ChevronDown className="size-3" /> : <ChevronUp className="size-3" />}
            {messages.length > 0 ? (
              <span className="ml-1 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-bold text-amber-300">
                {messages.length}
              </span>
            ) : null}
          </button>

          <DrawerComposer
            value={draft}
            onChange={setDraft}
            onSubmit={handleSubmit}
            disabled={submitting}
            placeholder={`Frag EVE zu ${pageTitle}…`}
            compact
          />
        </div>
      </div>
    </>
  );
}

// ── Sub-Components ─────────────────────────────────────────────────────────

function EmptyHint({
  pageTitle,
  pageContext,
}: {
  pageTitle: string;
  pageContext: string | null;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <Sparkles className="mb-2 size-6 text-amber-400/60" />
      <div className="text-sm text-neutral-300">
        EVE ist hier auf <span className="font-medium text-amber-300">{pageTitle}</span>.
      </div>
      {pageContext ? (
        <div className="mt-1 max-w-md text-[12px] leading-relaxed text-neutral-500">
          {pageContext}
        </div>
      ) : null}
      <div className="mt-3 text-[11px] text-neutral-600">
        Frag was du willst — sie sieht was du hier siehst.
      </div>
    </div>
  );
}

interface DrawerComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (text: string) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  compact?: boolean;
}

function DrawerComposer({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
  compact,
}: DrawerComposerProps) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) void onSubmit(value);
    }
  };

  if (compact) {
    return (
      <div className="flex flex-1 items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 rounded-lg border border-white/5 bg-neutral-900/60 px-3 py-2 text-[14px] text-neutral-100 placeholder:text-neutral-500 outline-none transition-colors focus:border-amber-500/40 focus:bg-neutral-900/80 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => value.trim() && !disabled && onSubmit(value)}
          disabled={!value.trim() || disabled}
          className="shrink-0 rounded-lg bg-amber-500/90 px-3 py-1.5 text-[11px] font-medium text-neutral-950 transition-all hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500"
        >
          Senden
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-end gap-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={2}
        disabled={disabled}
        className="scroll-hidden flex-1 resize-none rounded-lg border border-white/5 bg-neutral-900/60 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500 outline-none transition-colors focus:border-amber-500/40 focus:bg-neutral-900/80 disabled:opacity-50"
      />
      <button
        type="button"
        onClick={() => value.trim() && !disabled && onSubmit(value)}
        disabled={!value.trim() || disabled}
        className="shrink-0 rounded-lg bg-amber-500/90 px-4 py-2 text-sm font-medium text-neutral-950 transition-all hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500"
      >
        Senden
      </button>
    </div>
  );
}

function DrawerChatBubble({
  message,
  userInitial,
}: {
  message: ChatMessage;
  userInitial: string;
}) {
  const isUser = message.role === 'user';
  const isError = !!message.error;
  const text = stripEveCallDirective(message.text);

  if (isUser) {
    return (
      <div className="flex items-start gap-2">
        <div className="grid size-6 shrink-0 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[10px] font-bold text-neutral-950">
          {userInitial.toUpperCase()}
        </div>
        <div className="rounded-xl rounded-tl-md bg-neutral-800/70 px-3 py-1.5">
          <p className="text-[14px] leading-relaxed whitespace-pre-wrap text-neutral-100">{message.text}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <img
        src="/eve-satoshi.png"
        alt="EVE"
        className={`size-6 shrink-0 rounded-full object-cover ring-1 ring-amber-500/40 ${
          message.streaming ? 'animate-pulse' : ''
        }`}
      />
      <div
        className={`max-w-[90%] rounded-xl rounded-tl-md bg-neutral-900/60 px-4 py-2.5 ${
          isError ? 'border border-rose-500/30' : ''
        }`}
      >
        {message.text === '' && message.streaming ? (
          <div className="flex items-center gap-1.5 text-[12px] text-neutral-400">
            <Sparkles className="size-3 animate-pulse text-amber-400" />
            <span>EVE denkt nach…</span>
          </div>
        ) : (
          <div className={`space-y-1.5 text-[14px] leading-relaxed ${isError ? 'text-rose-300' : 'text-neutral-100'}`}>
            <MarkdownText text={text} />
            {message.streaming ? (
              <span className="ml-0.5 inline-block size-1 animate-pulse rounded-full bg-amber-400 align-middle" />
            ) : null}
            {message.citations && message.citations.length > 0 ? (
              <CitationList citations={message.citations} />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function CitationList({ citations }: { citations: ReadonlyArray<{ url: string; title?: string }> }) {
  // Doc-Feedback 2026-05-17: „Quellen irritieren, versteck sie hinter Button".
  // Default zugeklappt — Klick auf den Pill öffnet die Liste.
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        aria-expanded={expanded}
        className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] text-neutral-500 transition-colors hover:border-amber-500/30 hover:bg-amber-500/10 hover:text-amber-300"
      >
        <Link2 className="size-2.5" />
        {citations.length} {citations.length === 1 ? 'Quelle' : 'Quellen'}
        {expanded ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />}
      </button>
      {expanded ? (
        <ol className="mt-2 space-y-0.5 border-t border-white/5 pt-2">
          {citations.map((c, i) => (
            <li key={c.url} className="text-[11px] leading-snug">
              <span className="mr-1 text-neutral-600">[{i + 1}]</span>
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex max-w-full items-baseline gap-1 text-amber-300/80 hover:text-amber-200 hover:underline"
              >
                <span className="truncate align-baseline">{c.title || hostnameOf(c.url)}</span>
                <ExternalLink className="size-2.5 shrink-0 self-center" />
              </a>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function stripEveCallDirective(text: string): string {
  return text.replace(/<!--EVE_CALL:[\s\S]*?-->\s*$/, '').trim();
}

function formatPageKey(key: string): string {
  const parts = key.split('/').filter(Boolean);
  if (parts.length === 0) return 'Home';
  return parts[parts.length - 1]!
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}
